import type { Metadata } from 'next';
import { BENZINGA_LOGO_URL, type MetaProps } from '@benzinga/seo';

export const BENZINGA_ORIGIN = 'https://www.benzinga.com';

export function normalizeScreenerPath(urlOrPath: string): string {
  let pathname = urlOrPath;
  try {
    pathname = new URL(urlOrPath, BENZINGA_ORIGIN).pathname;
  } catch {
    pathname = urlOrPath.split(/[?#]/)[0] ?? urlOrPath;
  }
  return pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname || '/';
}

export interface ScreenerListItem {
  name: string;
  url: string;
}

export function rankingStocksToListItems(
  stocks: Array<{ name?: string | null; symbol?: string | null }>,
): ScreenerListItem[] {
  const items: ScreenerListItem[] = [];

  for (const stock of stocks) {
    const symbol = stock.symbol?.trim();
    if (!symbol) continue;
    const name = stock.name?.trim();
    items.push({
      name: name ? `${name} (${symbol})` : symbol,
      url: `${BENZINGA_ORIGIN}/quote/${symbol}`,
    });
  }

  return items;
}

export function buildScreenerItemList(items: ScreenerListItem[]): Record<string, unknown> | undefined {
  if (items.length === 0) return undefined;

  return {
    '@type': 'ItemList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      name: item.name,
      position: index + 1,
      url: item.url,
    })),
  };
}

export function buildScreenerMetadata(metaProps: MetaProps): Metadata {
  const title = metaProps.title;
  const description = metaProps.description ?? undefined;
  const url = metaProps.canonical ? String(metaProps.canonical) : undefined;
  const ogImage = String(metaProps.ogImage ?? metaProps.image ?? BENZINGA_LOGO_URL);
  const isDefaultImage = ogImage === BENZINGA_LOGO_URL;

  return {
    title,
    description,
    alternates: { canonical: url },
    openGraph: {
      title: metaProps.ogTitle ?? title,
      description: metaProps.ogDescription ?? description,
      siteName: 'Benzinga',
      locale: 'en_US',
      type: 'website',
      url,
      images: [
        {
          alt: isDefaultImage ? 'Benzinga Logo' : title,
          height: metaProps.ogImageHeight ?? (isDefaultImage ? 300 : 630),
          url: ogImage,
          width: metaProps.ogImageWidth ?? (isDefaultImage ? 400 : 1200),
        },
      ],
    },
    twitter: {
      card: metaProps.twitterCard ?? (isDefaultImage ? 'summary' : 'summary_large_image'),
      site: '@benzinga',
      title: metaProps.twitterTitle ?? title,
      description: metaProps.twitterDescription ?? description,
      images: [String(metaProps.twitterImage ?? ogImage)],
    },
  };
}

export function buildScreenerMetaProps(
  metaProps: MetaProps,
  pathname: string,
  listItems: ScreenerListItem[] = [],
): MetaProps {
  const canonical = metaProps.canonical ?? `${BENZINGA_ORIGIN}${pathname}`;
  const itemList = buildScreenerItemList(listItems);

  return {
    ...metaProps,
    canonical,
    dimensions: metaProps.dimensions ?? {
      authorName: 'Benzinga',
      contentType: 'tool',
    },
    structuredData: {
      ...metaProps.structuredData,
      pageType: 'CollectionPage',
      ...(itemList ? { mainEntity: itemList } : {}),
    },
  };
}
